import { useMutation, useQuery, useQueryClient } from "react-query";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import axiosClient from "../../../../../../lib/axios";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import { BoxProps, Grid, Paper, Stack, Typography } from "@mui/material";
import { TCheque } from "../../../../../../types/api/financeiro/detalhar-cheques";
import { FormProvider, useForm } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import moment from "moment";
import CardDadosCheque from "./DadosCheque/CardDadosCheque";
import CardDadosPagamento from "./DadosPagamento/CardDadosPagamento";
import { formatarMonetárioParaNúmero } from "../../../../../../helpers/masks";

export default function DialogDetalheCheque({
  open,
  id,
  handleClose,
}: DialogDetalheChequeProps) {
  const queryClient = useQueryClient();

  //querys e mutations
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["detalhar-cheque", id],
    queryFn: () => axiosClient.get(`financeiro/cheques/detalhar-cheque/${id}`),
    onSuccess: (data) => {
      setValue("detalhe", data?.data.data);
    },
  });
  const detalhe: TCheque = data?.data.data || null;

  const { mutate: atualizarCheque } = useMutation({
    mutationKey: "atualizar-cheque",
    mutationFn: (data: any) =>
      axiosClient.put(`financeiro/cheques/atualizar-cheque/${id}`, data),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar cheque", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(
        "table-data-grid-financeiro/cheques/pesquisar-cheques"
      );
      enqueueSnackbar("Cheque atualizado com sucesso!", { variant: "success" });
      refetch();
    },
    onSettled: () => {
      setValue("loading", false);
    },
  });
  //

  const onSubmit = (data: FormChequeProps) => {
    const payload = {
      tipo_publico: data.detalhe.dadosCheque.publico.tipo_publico,
      codigo_publico: data.detalhe.dadosCheque.publico.codigo_publico,
      cpf_cnpj: data.detalhe.dadosCheque.documento,
      banco_id: data.detalhe.dadosCheque.banco.codigo,
      agencia: data.detalhe.dadosCheque.agencia,
      agencia_digito: data.detalhe.dadosCheque.agencia_digito,
      conta: data.detalhe.dadosCheque.conta,
      conta_digito: data.detalhe.dadosCheque.conta_digito,
      numero_cheque: data.detalhe.dadosCheque.numero_cheque,
      sequencia_cheque: data.detalhe.dadosCheque.sequencia_cheque,
      lote: data.detalhe.dadosPagamento.lote,
      data_vencimento: moment(
        data.detalhe.dadosPagamento.data_vencimento,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD"),
      valor: formatarMonetárioParaNúmero(data.detalhe.dadosPagamento.valor),
      departamento_centro_custo_id:
        data.detalhe.dadosPagamento.departamento_centro_custo.id,
      conta_devedora_codigo:
        data.detalhe.dadosPagamento.conta_devedora.codigo_contabil,
      ...(data.detalhe.dadosPagamento.conta_devedora.centro_custo && {
        conta_devedora_custo:
          data.detalhe.dadosPagamento.conta_devedora.centro_custo.id,
      }),
      contra_partida_codigo:
        data.detalhe.dadosPagamento.contra_partida.codigo_contabil,
      ...(data.detalhe.dadosPagamento.contra_partida.centro_custo && {
        contra_partida_custo:
          data.detalhe.dadosPagamento.contra_partida.centro_custo.id,
      }),

      praca: data.detalhe.dadosPagamento.praca,
      data_origem: moment(
        data.detalhe.dadosPagamento.data_origem,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD"),
      ...(data.detalhe.dadosPagamento.loja && {
        loja_id: data.detalhe.dadosPagamento.loja.id,
      }),
    };
    atualizarCheque(payload);
  };

  const methods = useForm<FormChequeProps>({
    defaultValues: {
      detalhe,
      onSubmit: onSubmit,
      loading: false,
    },
  });

  const { setValue } = methods;

  const boxStyle: BoxProps = {
    component: Paper,
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  };

  return (
    <FormProvider {...methods}>
      <DialogFullScreen {...{ open, handleClose }} title="Detalhes do Cheque">
        {isLoading && <LoadingApiQuery />}

        {!isLoading && detalhe && (
          <>
            <Grid container spacing={2} justifyContent={"center"}>
              <Grid
                container
                size={{xs: 12, md: 8}}
                spacing={2}
                alignSelf="flex-start"
              >
                <Grid size={{xs: 12}}>
                  <CardDadosCheque {...{ boxStyle, detalhe }} />
                </Grid>

                <Grid size={{xs: 12}}>
                  <CardDadosPagamento {...{ boxStyle, detalhe }} />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </DialogFullScreen>
    </FormProvider>
  );
}

export const DadosChequeItems = ({ titulo, valor }: any) => {
  return (
    <Stack>
      <Typography fontWeight="bold" variant="subtitle2">
        {titulo}
      </Typography>
      <Typography variant="subtitle2">{valor}</Typography>
    </Stack>
  );
};

interface DialogDetalheChequeProps {
  open: boolean;
  id: string;
  handleClose: () => void;
}

export interface FormChequeProps {
  detalhe: TCheque;
  onSubmit: (data: TCheque) => void;
  loading: boolean;
}
